
import {
  barcodeDectectorPolyfill1DScanditDataCaptureFormat,
  barcodeDectectorPolyfill2DScanditDataCaptureFormat,
  barcodeReaderPolyFill,
  default1DFormat,
  default1DScanditDataCaptureFormat,
  default2DFormat,
  default2DScanditDataCaptureFormat,
  defaultBarcodeReader,
  defaultBarcodeReaderPolyFillFormat,
  defaultFormat,
  scanditDataCapture,
} from '@/utils/barcodeReader'
import { BARCODE_SCAN, CURRENT_CAMERA_KEY, LOCAL_STORAGE_ITEM } from '@/utils/constants'
import { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType } from '@zxing/library'
import { BarcodeFormat as BarcodeReaderPolyFillFormat } from 'barcode-detector/pure'
import { BarcodeCapture, BarcodeCaptureSettings, Symbology, SymbologySettings } from 'scandit-web-datacapture-barcode'
import { DataCaptureContext, FrameSourceState } from 'scandit-web-datacapture-core'
import { AVAILABLE_DELIMITERS, EBCReaderCategory, EBCRType } from 'smartbarcode-web-core/src/utils/enums/index'

import { stopStreams } from '@/utils/helpers'
import { Options, Vue } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

@Options({
  components: {},
  name: 'BarcodeReaderMixin',
})
export default class BarcodeReaderMixin extends Vue {
  privateSymbology = ''
  context = {}
  valueCode = ''
  multipleScan = false
  isHandleMultipleScan = false
  showMultipleScan = false
  delimiterValue = AVAILABLE_DELIMITERS[0]
  activeStreams = [] as MediaStream[]
  cameras = [] as MediaDeviceInfo[]
  selectedCamera = ''
  isOpenDialog = false
  componentMounted = false

  get isShowMultipleScan() {
    this.showMultipleScan =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.isEnable ?? false
    return this.showMultipleScan
  }

  get delimiter() {
    this.delimiterValue =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.delimiter ??
      AVAILABLE_DELIMITERS[0]
    return this.delimiterValue
  }

  get symbologies() {
    const lastSelectedSymbology = localStorage.getItem(this.localStorageItem)
    if (!lastSelectedSymbology && !this.privateSymbology) {
      localStorage.setItem(this.localStorageItem, EBCReaderCategory.DEFAULT)
      this.privateSymbology = EBCReaderCategory.DEFAULT
      return this.privateSymbology
    }

    if (this.privateSymbology) return this.privateSymbology

    switch (lastSelectedSymbology) {
      case EBCReaderCategory.ONE_DIMENSION:
      case EBCReaderCategory.QR:
      case EBCReaderCategory.DEFAULT:
        this.privateSymbology = lastSelectedSymbology
        return this.privateSymbology
      default:
        localStorage.setItem(this.localStorageItem, EBCReaderCategory.DEFAULT)
        this.privateSymbology = EBCReaderCategory.DEFAULT
        return this.privateSymbology
    }
  }

  set symbologies(symbology: string) {
    this.privateSymbology = symbology
    localStorage.setItem(this.localStorageItem, symbology)
  }

  get localStorageItem() {
    return `[${this.$route.name}]_${LOCAL_STORAGE_ITEM.LAST_SELECTED_SYMBOLOGY}`
  }

  get currentCameraId() {
    return localStorage.getItem(CURRENT_CAMERA_KEY) ?? ''
  }

  @Watch('selectedCamera')
  async setCameraId() {
    this.isOpenDialog = false
    this.vidOff()
    localStorage.setItem(CURRENT_CAMERA_KEY, this.selectedCamera)
    await this.setupCameraSetting()
  }

  async mounted() {
    this.componentMounted = true
  }

  async initCameraSelection() {
    const devices = await navigator.mediaDevices.enumerateDevices()
    this.cameras = devices.filter((device) => device.kind === 'videoinput')
    if (this.currentCameraId) {
      const currentCamera = this.cameras.filter((device: MediaDeviceInfo) => device.deviceId === this.currentCameraId)
      if (currentCamera) {
        this.selectedCamera = this.currentCameraId
      }
    }
  }

  openDialog() {
    this.isOpenDialog = true
  }

  closeDialog() {
    this.isOpenDialog = false
  }

  barcodeDataCaptureContext(context: DataCaptureContext, barcodeDataCapture: BarcodeCapture) {
    this.loading = false
    this._barcodeDataCapture = barcodeDataCapture
    this.context = context
  }

  get zxingInstance(): BrowserMultiFormatReader {
    return this.codeReader
  }

  get barcodeReaderType(): EBCRType {
    return this.$store.getters.barcodeReaderType
  }

  async initScanditReader() {
    const enableSymbologies = []
    if (this.symbologies === EBCReaderCategory.QR) {
      enableSymbologies.push(...default2DScanditDataCaptureFormat)
    }

    if (this.symbologies === EBCReaderCategory.ONE_DIMENSION) {
      enableSymbologies.push(...default1DScanditDataCaptureFormat)
    }

    if (this.symbologies === EBCReaderCategory.DEFAULT) {
      enableSymbologies.push(...default1DScanditDataCaptureFormat, ...default2DScanditDataCaptureFormat)
    }
    const video = document.getElementById('video') as HTMLVideoElement
    if (video) {
      video.style.display = 'none'
    }
    await scanditDataCapture(
      this.scanditDataResult,
      document.getElementById('scan-area') || document.createElement('div'),
      true,
      this.barcodeDataCaptureContext,
      enableSymbologies
    )
  }

  async initBarcodeReaderPolyFill(barcodeFormat?: BarcodeReaderPolyFillFormat[]) {
    const enableSymbologies: BarcodeReaderPolyFillFormat[] = []
    if (this.symbologies === EBCReaderCategory.QR) {
      enableSymbologies.push(...barcodeDectectorPolyfill2DScanditDataCaptureFormat)
    }

    if (this.symbologies === EBCReaderCategory.ONE_DIMENSION) {
      enableSymbologies.push(...barcodeDectectorPolyfill1DScanditDataCaptureFormat)
    }

    if (this.symbologies === EBCReaderCategory.DEFAULT) {
      enableSymbologies.push(
        ...barcodeDectectorPolyfill1DScanditDataCaptureFormat,
        ...barcodeDectectorPolyfill2DScanditDataCaptureFormat
      )
    }
    await this.setupVideo()
    if (!this.video?.srcObject) return
    await barcodeReaderPolyFill(
      this.barcodeDetectorPolyFillScanReader,
      (document.getElementById('scan-area') as HTMLVideoElement) || document.createElement('div'),
      this.video.srcObject as MediaStream,
      true,
      barcodeFormat || enableSymbologies || defaultBarcodeReaderPolyFillFormat
    )
  }

  async initZxingReader(barcodeFormat?: BarcodeFormat[]) {
    const enableSymbologies = []
    if (this.symbologies === EBCReaderCategory.QR) {
      enableSymbologies.push(...default2DFormat)
    }

    if (this.symbologies === EBCReaderCategory.ONE_DIMENSION) {
      enableSymbologies.push(...default1DFormat)
    }

    if (this.symbologies === EBCReaderCategory.DEFAULT) {
      enableSymbologies.push(...default1DFormat, ...default2DFormat)
    }
    await this.setupVideo()
    if (!this.video?.srcObject) return
    await defaultBarcodeReader(
      this.defaultScanReader,
      (document.getElementById('scan-area') as HTMLVideoElement) || document.createElement('div'),
      this.video.srcObject as MediaStream,
      true,
      barcodeFormat || enableSymbologies || defaultFormat
    )
  }

  async setBarcodeSymbologies(selection: string) {
    switch (this.barcodeReaderType) {
      case EBCRType.SCANDIT:
        this.applyScanditSymbologies(selection)
        break
      case EBCRType.BARCODE_DETECTION:
        await this.applyBarcodeReaderPollySymbologies(selection)
        break
      default:
        await this.applyXzingSymbologies(selection)
        break
    }
  }

  async applyXzingSymbologies(selection: string) {
    const enableSymbologies: BarcodeFormat[] = []
    if (selection === EBCReaderCategory.QR) {
      enableSymbologies.push(...default2DFormat)
    }

    if (selection === EBCReaderCategory.ONE_DIMENSION) {
      enableSymbologies.push(...default1DFormat)
    }

    if (selection === EBCReaderCategory.DEFAULT) {
      enableSymbologies.push(...default1DFormat, ...default2DFormat)
    }
    const hints = new Map()
    hints.set(DecodeHintType.POSSIBLE_FORMATS, enableSymbologies)
    this.zxingInstance.hints = hints
    this.zxingInstance.reset()
    await this.initZxingReader(enableSymbologies)
  }

  async applyBarcodeReaderPollySymbologies(selection: string) {
    const enableSymbologies: BarcodeReaderPolyFillFormat[] = []
    if (selection === EBCReaderCategory.QR) {
      enableSymbologies.push(...barcodeDectectorPolyfill2DScanditDataCaptureFormat)
    }

    if (selection === EBCReaderCategory.ONE_DIMENSION) {
      enableSymbologies.push(...barcodeDectectorPolyfill1DScanditDataCaptureFormat)
    }

    if (selection === EBCReaderCategory.DEFAULT) {
      enableSymbologies.push(
        ...barcodeDectectorPolyfill1DScanditDataCaptureFormat,
        ...barcodeDectectorPolyfill2DScanditDataCaptureFormat
      )
    }
    await barcodeReaderPolyFill(
      this.barcodeDetectorPolyFillScanReader,
      (document.getElementById('scan-area') as HTMLVideoElement) || document.createElement('div'),
      this.video.srcObject as MediaStream,
      true,
      enableSymbologies || defaultBarcodeReaderPolyFillFormat
    )
  }

  async applyScanditSymbologies(selection: string) {
    const currentScanner = this._barcodeDataCapture
    if (!currentScanner) return
    const enableSymbologies: Symbology[] = []
    if (selection === EBCReaderCategory.QR) {
      enableSymbologies.push(...default2DScanditDataCaptureFormat)
    }

    if (selection === EBCReaderCategory.ONE_DIMENSION) {
      enableSymbologies.push(...default1DScanditDataCaptureFormat)
    }

    if (selection === EBCReaderCategory.DEFAULT) {
      enableSymbologies.push(...default1DScanditDataCaptureFormat, ...default2DScanditDataCaptureFormat)
    }

    const symbologies = [...default1DScanditDataCaptureFormat, ...default2DScanditDataCaptureFormat]
    const settings: BarcodeCaptureSettings = new BarcodeCaptureSettings()

    // enable all symbologies
    settings.enableSymbologies(symbologies)

    const codabarSetting: SymbologySettings = settings.settingsForSymbology(Symbology.Codabar)
    codabarSetting.activeSymbolCounts = [
      1,
      2,
      3,
      4,
      5,
      67,
      8,
      9,
      10,
      11,
      12,
      13,
      14,
      15,
      16,
      17,
      18,
      19,
      20,
      21,
      22,
      23,
      24,
      25,
      26,
      27,
      28,
      29,
      30,
    ]

    settings.codeDuplicateFilter = BARCODE_SCAN.timeBetweenScansMillis || 500

    symbologies.forEach((element) => {
      // disable not use symbologies
      if (!enableSymbologies.includes(element)) {
        settings.enableSymbology(element, false)
      }
    })
    await (currentScanner as BarcodeCapture).applySettings(settings)
  }

  vidOff() {
    stopStreams(this.activeStreams)
  }

  async beforeUnmount() {
    this.componentMounted = false
    if (this.zxingInstance) this.zxingInstance.reset()
    if (this.context && this._barcodeDataCapture) {
      await this._barcodeDataCapture.setEnabled(false)
      await (this.context as DataCaptureContext).frameSource?.switchToDesiredState(FrameSourceState.Off)
      await (this.context as DataCaptureContext).dispose()
    }
    this.vidOff()
  }

  get isMultipleScan() {
    const storedValue = localStorage.getItem('[multiple-scan]_is_multiple_scan')
    if (!this.isShowMultipleScan) {
      localStorage.setItem('[multiple-scan]_is_multiple_scan', 'false')
      return false
    }

    if (!storedValue) {
      localStorage.setItem('[multiple-scan]_is_multiple_scan', this.multipleScan.toString())
      return this.multipleScan
    }

    this.multipleScan = storedValue.toLowerCase() === 'true'
    return this.multipleScan
  }

  set isMultipleScan(value: boolean) {
    localStorage.setItem('[multiple-scan]_is_multiple_scan', value.toString())
    this.multipleScan = value
  }

  handleUpdateValueCode(value: string) {
    this.valueCode = value
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = this.valueCode + this.delimiter + value
    } else {
      result = value
    }
    return result.trim()
  }

  @Watch('isMultipleScan')
  onMultipleScanModeChange() {
    if (this.isMultipleScan === false && this.valueCode !== '') {
      this.$confirm(this.$t('change_camera_mode_confirm'), this.$t('Warning'), {
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'danger',
        cancelButtonText: this.$t('cancel'),
        // showClose: !isForce,
        closeOnClickModal: true,
        showCancelButton: true,
      })
        .then(() => {
          this.resetScannedValue()
        })
        .catch(() => {
          this.isMultipleScan = true
        })
    }
  }

  getContentDialog(plainText: string) {
    if (this.valueCode.trim().includes(plainText.trim())) {
      return `${this.$t('confirm_scanned_value', { item: plainText })}<br>
     <br>
      <i class="el-icon-warning-outline" style="color: #e6a23c"></i>
      <span style="color: #e6a23c">${this.$t('warning_already_scanned')} ${this.$t('continue_confirmation')}</span>`
    }

    return this.$t('confirm_scanned_value', { item: plainText })
  }

  async setStream(stream: MediaStream) {
    this.activeStreams.push(stream)
    // Setup the video stream
    if (this.video) {
      this.video.srcObject = stream
      const streamSetting = stream.getVideoTracks()[0].getSettings()
      // actual width & height of the camera video
      this.pictureWidth = streamSetting.width || this.pictureWidth
      this.pictureHeight = streamSetting.height || this.pictureHeight
    }
  }
}
